import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// ─── Fetch with timeout ────────────────────────────────────────
// Wrap every Supabase HTTP request (PostgREST + Auth + Storage) with a
// 15-second AbortController so a hanging token refresh or slow connection
// never leaves the UI stuck on a loading spinner indefinitely.
// The finally{set({loading:false})} blocks in every store/page will run
// when the AbortError is thrown, clearing the spinner immediately.
const FETCH_TIMEOUT_MS = 15_000;

function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  return fetch(input as RequestInfo, { ...init, signal: ctrl.signal })
    .finally(() => clearTimeout(id));
}

// ─── In-memory auth lock (replaces navigator.locks) ────────────
// Supabase JS v2 defaults to `navigator.locks.request()` for serializing
// auth-token refreshes across browser tabs. That API is the root cause of
// the "infinite spinner after switching windows" bug:
//
//   1. User switches to another window → page becomes hidden.
//   2. The web-locks lock named `lock:sb-<ref>-auth-token` is held by a
//      pending refresh, but the browser pauses timers on the hidden tab,
//      so the holder never releases.
//   3. User comes back → every PostgREST query queues up waiting for the
//      lock and stays pending until the OS-level lock eventually times out
//      (~30 s) or the user reloads the page.
//
// This SPA does not need cross-tab session synchronisation (each tab manages
// its own session via `persistSession: true` + `localStorage`). Replacing the
// lock with a per-instance in-memory mutex keeps refreshes serialised within
// a single tab without depending on the flaky web-locks API.
//
// Reference: https://github.com/supabase/auth-js/issues/768
let authLockChain: Promise<unknown> = Promise.resolve();
const inMemoryLock = async <R,>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
  const run = authLockChain.then(() => fn());
  // Swallow rejections in the chain so one failure doesn't poison subsequent acquisitions.
  authLockChain = run.catch(() => undefined);
  return run;
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    // Use the timeout wrapper for all HTTP calls made by this client.
    fetch: fetchWithTimeout,
  },
  auth: {
    // 'implicit' avoids PKCE code-exchange locks that can pile up in React StrictMode.
    flowType: 'implicit',
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
    // Override the default navigator.locks-based lock — see comment above.
    lock: inMemoryLock,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// ─── Visibility-based auth refresh + Realtime reconnect ────────
// When the tab is hidden, browsers throttle timers heavily, which:
//   • Causes pending `setTimeout`-based refresh attempts to stall.
//   • Makes the Realtime WebSocket get killed by the OS after ~30-60 s idle.
//
// On hide:  stop auto-refresh so no half-finished refresh sits waiting for
//           a throttled timer to fire.
// On show:  start auto-refresh again (which immediately checks expiry) and
//           reconnect Realtime if the socket has died.

let hiddenAt = 0;

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    // Pause auto-refresh so we don't leave a half-running refresh chained
    // behind a throttled timer.
    supabase.auth.stopAutoRefresh().catch(() => {});
    return;
  }

  // Tab is now visible again. Always restart auto-refresh — this triggers
  // an immediate session check and refresh if the token is close to expiry.
  supabase.auth.startAutoRefresh().catch(() => {});

  // Only reconnect Realtime if the tab was hidden long enough that the
  // socket likely died.
  if (hiddenAt > 0 && Date.now() - hiddenAt > 5_000) {
    hiddenAt = 0;
    const rt = supabase.realtime;
    // Only reconnect if the socket is fully closed — skip CONNECTING/CLOSING
    // states to avoid racing with an in-progress connection attempt.
    if (rt.conn == null || rt.conn.readyState === WebSocket.CLOSED) {
      console.info('[Supabase] Realtime WS closed after tab return — reconnecting');
      rt.connect(); // does NOT clear channels; they will re-subscribe automatically
    }
  } else {
    hiddenAt = 0;
  }
});

